import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell } from "recharts";
import analyticsService from "../../../services/university-admin/analyticsService";

const COLORS = ["#4361ee", "#3f37c9", "#4895ef", "#4cc9f0", "#f72585", "#b5179e"];

const UMAnalyticsDashboard = ({ filters }) => {
  const { t } = useTranslation(['universityManager', 'users']);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      const res = await analyticsService.getDashboard(filters);
      if (res.success) {
        setData(res.data);
      } else {
        setData(null);
      }
      setLoading(false);
    };
    fetchDashboard();
  }, [filters]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="dashboard-content">
      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body d-flex flex-column justify-content-center">
              <h6 className="text-muted fw-semibold mb-2">{t("analytics.dashboard.totalEvents")}</h6>
              <h2 className="mb-0 fw-bold text-dark">{data.kpis?.totalEvents || 0}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body d-flex flex-column justify-content-center">
              <h6 className="text-muted fw-semibold mb-2">{t("analytics.dashboard.avgParticipation")}</h6>
              <h2 className="mb-0 fw-bold text-dark">{data.kpis?.avgParticipation || 0}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body d-flex flex-column justify-content-center">
              <h6 className="text-muted fw-semibold mb-2">{t("analytics.dashboard.topCategory")}</h6>
              <h2 className="mb-0 fw-bold fs-4 text-primary text-truncate" title={data.kpis?.topCategory}>{data.kpis?.topCategory || "-"}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body d-flex flex-column justify-content-center">
              <h6 className="text-muted fw-semibold mb-2">{t("analytics.dashboard.mostActiveClub")}</h6>
              <h2 className="mb-0 fw-bold fs-4 text-primary text-truncate" title={data.kpis?.mostActiveClub}>{data.kpis?.mostActiveClub || "-"}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row g-4 mb-4">
        {/* Events Over Time (Line Chart) */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body">
              <h5 className="card-title fw-bold mb-4">{t("analytics.dashboard.eventsOverTime")}</h5>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.eventsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8f9fa" />
                    <XAxis dataKey="bucket" axisLine={false} tickLine={false} tick={{ fill: '#adb5bd', fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#adb5bd', fontSize: 12 }} dx={-10} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Line type="monotone" dataKey="count" stroke="#4361ee" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6, fill: '#4361ee', stroke: '#fff', strokeWidth: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Events by Category (Donut Chart) */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body">
              <h5 className="card-title fw-bold mb-4">{t("analytics.dashboard.eventsByCategory")}</h5>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.eventsByCategory}
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="label"
                    >
                      {data.eventsByCategory?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Participation by Department (Stacked Bar Chart) */}
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body">
              <h5 className="card-title fw-bold mb-4">{t("analytics.dashboard.participationByDepartment")}</h5>
              <div style={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pivotParticipationData(data.participationByDepartment)} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8f9fa" />
                    <XAxis dataKey="department" axisLine={false} tickLine={false} tick={{ fill: '#adb5bd', fontSize: 12 }} angle={-45} textAnchor="end" dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#adb5bd', fontSize: 12 }} dx={-10} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="PREPARATORY" name={t("users:grade.PREPARATORY")} stackId="a" fill="#4361ee" />
                    <Bar dataKey="FIRST" name={t("users:grade.FIRST")} stackId="a" fill="#3f37c9" />
                    <Bar dataKey="SECOND" name={t("users:grade.SECOND")} stackId="a" fill="#4895ef" />
                    <Bar dataKey="THIRD" name={t("users:grade.THIRD")} stackId="a" fill="#4cc9f0" />
                    <Bar dataKey="FOURTH" name={t("users:grade.FOURTH")} stackId="a" fill="#f72585" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper to pivot the flat data {department, grade, count} into {department, grade1: count1, grade2: count2}
const pivotParticipationData = (data) => {
  if (!data) return [];
  const pivoted = {};
  data.forEach(item => {
    if (!pivoted[item.department]) {
      pivoted[item.department] = { department: item.department };
    }
    pivoted[item.department][item.grade] = item.count;
  });
  return Object.values(pivoted);
};

export default UMAnalyticsDashboard;
